import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl, statusList } from "../../../comman/config";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';

// Add/Edit User. Department is a multi-select and Role a single one, per the client's sketch.
//
// Login ID and Name are two fields, not one. The Login ID is what a person types to sign in; the
// Name is who they are. Only the SuperAdmin may change a Login ID once it exists -- it is an
// identity, and a user who renames their own login has locked themselves out of the only thing
// they remember. The field is set freely when the account is created, and read-only after.
//
// `readOnly` is View: every field disabled and no Submit. A user whose operations on User Master
// stop at View reaches this through /user_master/view/:id, which is the same screen with nothing
// on it that writes.
export default function UserMasterForm({ detail, page = 'user_master', readOnly = false }) {

    const [formData, setFormData] = useState({
        username: '',
        name: '',
        mobile: '',
        department: [],
        role: null,
        center: '',
        mht_id: '',
        password: '',
        status: 1,
    });
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [validationErrors, setValidationErrors] = useState({
        username: false,
        name: false,
        mobile: false,
        role: false,
    });
    // Which password fields are readable. Keyed by field, so the form can gain another without
    // the two sharing one switch.
    const [shown, setShown] = useState({});
    const toggleShown = (name) => setShown((prev) => ({ ...prev, [name]: !prev[name] }));
    const [saveError, setSaveError] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const formRef = useRef(null);

    const navigate = useNavigate();
    const role = String(localStorage.getItem('userRole'));
    // Editing an existing account: the Login ID is the SuperAdmin's to change and nobody else's.
    // The server refuses a changed one from anyone else, so this only saves the round trip.
    const loginIdLocked = Boolean(detail) && role !== '0';

    useEffect(() => {
        const getOptions = async () => {
            try {
                const [departments, roles] = await Promise.all([
                    axios.get(`${baseUrl}/department_master/list`),
                    axios.get(`${baseUrl}/role_master/list`),
                ]);
                // Active departments, and never department 0: "No Department" is where a SCREEN
                // waits for a real department, not a department a person can be put in.
                setDepartmentOptions((departments.data?.data ?? [])
                    .filter((department) => Number(department.status) === 1 && Number(department.id) !== 0)
                    .map((department) => ({ value: department.id, label: department.name })));
                setRoleOptions((roles.data?.data ?? [])
                    .filter((option) => Number(option.status) === 1 || Number(option.id) === 0)
                    .map((option) => ({ value: option.id, label: option.name })));
            } catch (error) {
                console.log(error);
            }
        };
        getOptions();
    }, []);

    // Options arrive from their own request, so the stored ids are turned into options only once
    // both are in hand -- otherwise an edit would open showing bare numbers.
    useEffect(() => {
        if (!detail) return;
        const user = detail[0];
        const departmentIds = String(user.department ?? '').split(',').filter((entry) => entry !== '');
        setFormData({
            username: user.username || '',
            name: user.name || '',
            mobile: user.mobile || '',
            department: departmentIds.map((id) => departmentOptions.find((option) => String(option.value) === id)
                ?? { value: Number(id), label: id }),
            role: roleOptions.find((option) => String(option.value) === String(user.role)) ?? null,
            center: user.center || '',
            mht_id: user.mht_id || '',
            // Never prefilled: the column holds a hash, and blank means "leave it alone".
            password: '',
            status: Number(user.status ?? 1),
        });
    }, [detail, departmentOptions, roleOptions]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        // No Submit is drawn in View. This is the stray Enter key in a field the browser left
        // focusable, and it saves nothing either.
        if (readOnly) return;
        const newValidationErrors = {
            username: formData.username.trim() === '',
            name: formData.name.trim() === '',
            mobile: formData.mobile.trim() === '',
            role: formData.role === null,
        };
        setValidationErrors(newValidationErrors);
        if (Object.values(newValidationErrors).some((error) => error)) {
            const firstInvalidInput = formRef.current.querySelector('.invalid');
            if (firstInvalidInput) firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setSaveError('');
        try {
            setPopupOpen(true);
            const url = detail ? `${page}/edit/${detail[0].id}` : `${page}/add`;
            await axios.post(`${baseUrl}/${url}`, {
                role,
                formData: {
                    ...formData,
                    department: formData.department.map((department) => department.value),
                    role: formData.role?.value,
                },
            });
            setPopupOpen(false);
            navigate(`/${page}/list`);
        } catch (error) {
            setPopupOpen(false);
            console.log(error);
            setSaveError(error?.response?.data?.msg ?? 'Could not save the user.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
        setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: false }));
    };

    const field = (name, label, { type = 'text', required = false, hint = '', disabled = false } = {}) => {
        const locked = disabled || readOnly;
        return (
            <div className="sm:col-span-2">
                <label htmlFor={name} className="block text-sm font-medium leading-6 text-gray-900">{label}{required ? ' *' : ''}</label>
                <div className="mt-2 relative">
                    <input
                        type={type === 'password' && shown[name] ? 'text' : type}
                        name={name}
                        id={name}
                        placeholder={label}
                        disabled={locked}
                        autoComplete={type === 'password' ? 'new-password' : 'off'}
                        value={formData[name]}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md border ${validationErrors[name] ? 'border-red-500 invalid' : 'border-gray-300'} py-1.5 pl-1.5 ${type === 'password' ? 'pr-10' : 'pr-1.5'} text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 ${locked ? 'bg-gray-200 text-gray-700' : ''}`}
                    />
                    {type === 'password' && !locked && (
                        <button
                            type="button"
                            onClick={() => toggleShown(name)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            aria-label={shown[name] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
                            tabIndex={-1}
                        >
                            {shown[name]
                                ? <EyeOffIcon className="h-5 w-5 text-gray-500" />
                                : <EyeIcon className="h-5 w-5 text-gray-500" />}
                        </button>
                    )}
                </div>
                {validationErrors[name]
                    ? <div className="text-red-500">Please enter {label.toLowerCase()}.</div>
                    : (hint ? <div className="text-xs text-gray-500 mt-1">{hint}</div> : null)}
            </div>
        );
    };

    return (
        <form ref={formRef}>
            <div className="space-y-12">
                <div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
                        {field('username', 'Login ID', {
                            required: true,
                            disabled: loginIdLocked,
                            hint: loginIdLocked
                                ? 'Only the SuperAdmin can change a login id.'
                                : 'What this person types to sign in.',
                        })}
                        {field('name', 'Name', { required: true })}
                        {field('mobile', 'Mobile', { required: true })}

                        <div className="sm:col-span-2">
                            <label htmlFor="department" className="block text-sm font-medium leading-6 text-gray-900">Department *</label>
                            <div className="mt-2">
                                <Select
                                    {...menuAboveTable}
                                    isMulti
                                    inputId="department"
                                    classNamePrefix="select"
                                    className="basic-multi-select"
                                    value={formData.department}
                                    options={departmentOptions}
                                    isDisabled={readOnly}
                                    onChange={(selected) => setFormData((prevData) => ({ ...prevData, department: selected ?? [] }))}
                                    placeholder="All"
                                />
                            </div>
                            {!readOnly && <div className="text-xs text-gray-500 mt-1">Leave empty for all departments.</div>}
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="role" className="block text-sm font-medium leading-6 text-gray-900">Role *</label>
                            <div className={`mt-2 ${validationErrors.role ? 'invalid' : ''}`}>
                                <Select
                                    {...menuAboveTable}
                                    inputId="role"
                                    classNamePrefix="select"
                                    value={formData.role}
                                    options={roleOptions}
                                    isDisabled={readOnly}
                                    onChange={(selected) => {
                                        setFormData((prevData) => ({ ...prevData, role: selected }));
                                        setValidationErrors((prevErrors) => ({ ...prevErrors, role: false }));
                                    }}
                                    placeholder="Select role"
                                />
                            </div>
                            {validationErrors.role && <div className="text-red-500">Please select a role.</div>}
                        </div>

                        {field('center', 'Center')}
                        {field('mht_id', 'MHT ID')}
                        {/* Not drawn in View: the column holds a hash, so there is nothing to
                            show, and a disabled empty box only invites the question. */}
                        {!readOnly && field('password', 'Password', {
                            type: 'password',
                            hint: detail ? 'Leave empty to keep the current password.' : 'Required for a new user.',
                        })}

                        <div className="sm:col-span-2">
                            <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">Status *</label>
                            <div className="mt-2">
                                <Select
                                    {...menuAboveTable}
                                    inputId="status"
                                    classNamePrefix="select"
                                    value={statusList.find((option) => option.value === Number(formData.status))}
                                    options={statusList}
                                    isDisabled={readOnly}
                                    onChange={(selected) => setFormData((prevData) => ({ ...prevData, status: selected.value }))}
                                    isClearable={false}
                                />
                            </div>
                        </div>
                    </div>
                    {saveError && <div className="text-red-500 mt-4">{saveError}</div>}
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-x-6">
                <Link to={`/${page}/list`}>
                    <button type="button" className="text-sm font-semibold leading-6 text-gray-900">{readOnly ? 'Back' : 'Cancel'}</button>
                </Link>
                {!readOnly && <button
                    onClick={handleSubmit}
                    disabled={popupOpen}
                    type="submit"
                    className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                    Submit
                </button>}
            </div>
        </form>
    )
}
